import { Link } from "@tanstack/react-router";
import { Clock, ArrowUpRight } from "lucide-react";
import { PageShell, PageHero } from "@/components/knmc/PageShell";
import type { ArticleListItem } from "@/hooks/useArticles";

export default function ArticlesList({ articles }: { articles: ArticleListItem[] }) {
  if (!articles.length) return <PageShell><div className="flex min-h-[50vh] items-center justify-center text-muted-foreground">No articles available.</div></PageShell>;
  const [featured, ...rest] = articles;
  return (
    <PageShell>
      <PageHero
        eyebrow="Health Articles"
        title="Trusted health insights from our doctors"
        description="Evidence-based articles on wellness, prevention, and care — written by KNMC's specialists for patients in Nepal."
      />
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-6">
          <Link
            to={`/health-articles/${featured.slug}`}
            className="group mb-12 grid overflow-hidden rounded-3xl border border-border bg-card transition-all hover:shadow-elevated md:grid-cols-2"
          >
            <div className="aspect-[4/3] overflow-hidden md:aspect-auto">
              <img src={featured.cover} alt={featured.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
            </div>
            <div className="flex flex-col justify-center p-10">
              <span className="text-xs font-semibold uppercase tracking-wider text-secondary">{featured.category}</span>
              <h2 className="mt-3 font-display text-3xl font-semibold text-primary md:text-4xl">{featured.title}</h2>
              <p className="mt-4 text-muted-foreground">{featured.excerpt}</p>
              <div className="mt-6 flex items-center gap-4 text-sm text-muted-foreground">
                <span>{featured.author}</span>
                <span>·</span>
                <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{featured.readTime}</span>
              </div>
            </div>
          </Link>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {rest.map((a) => (
              <Link
                key={a.slug}
                to={`/health-articles/${a.slug}`}
                className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card transition-all hover:-translate-y-1 hover:shadow-elevated"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={a.cover} alt={a.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <span className="text-xs font-semibold uppercase tracking-wider text-secondary">{a.category}</span>
                  <h3 className="mt-2 font-display font-space text-xl font-semibold text-foreground">{a.title}</h3>
                  <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{a.excerpt}</p>
                  <div className="mt-auto flex items-center justify-between pt-5 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{a.readTime}</span>
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PageShell>
  );
}
